import React, { type ReactNode, createContext, useContext } from 'react';

import type { ForYouNavigationActions, ForYouNavigationState } from './types';

const DEFAULT_STATE: ForYouNavigationState = {
	currentView: { type: 'threads-list' },
};

// resetNavigation is a no-op — only one view exists today.
// Kept for API compatibility with external consumers (e.g. conversation-assistant)
// and to support potential future navigation states without a breaking change.
const DEFAULT_ACTIONS: ForYouNavigationActions = {
	resetNavigation: () => {},
};

const ForYouNavigationStateContext = createContext<ForYouNavigationState>(DEFAULT_STATE);
const ForYouNavigationActionsContext = createContext<ForYouNavigationActions>(DEFAULT_ACTIONS);

export function ForYouNavigationProvider({ children }: { children: ReactNode }): React.JSX.Element {
	return (
		<ForYouNavigationStateContext.Provider value={DEFAULT_STATE}>
			<ForYouNavigationActionsContext.Provider value={DEFAULT_ACTIONS}>
				{children}
			</ForYouNavigationActionsContext.Provider>
		</ForYouNavigationStateContext.Provider>
	);
}

export type ForYouNavigation = ForYouNavigationState & ForYouNavigationActions;

export function useForYouNavigationState(): ForYouNavigationState {
	return useContext(ForYouNavigationStateContext);
}

export function useForYouNavigationActions(): ForYouNavigationActions {
	return useContext(ForYouNavigationActionsContext);
}

export function useForYouNavigation(): ForYouNavigation {
	const state = useForYouNavigationState();
	const actions = useForYouNavigationActions();
	return { ...state, ...actions };
}
